import { gql, type TypedDocumentNode } from '@apollo/client';
import { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid/index.tsx';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { ReferrerCodeCountdownChip } from '@sorare/core/src/components/referral/ReferrerCodeCountdownChip/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useGetCardSetConfig } from '@sorare/core/src/set/index.ts';

import { RefereeExclusiveReward } from '#src/components/series/inviteFriends/RefereeExclusiveReward/index.tsx';

import type {
  AddReferrerCodeMutation,
  AddReferrerCodeMutationVariables,
  SetInviteCodeDialogQuery,
  SetInviteCodeDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const SET_INVITE_CODE_DIALOG_QUERY = gql`
  query SetInviteCodeDialogQuery {
    currentUser {
      slug
      referrerCodeDeadline
    }
  }
` as TypedDocumentNode<
  SetInviteCodeDialogQuery,
  SetInviteCodeDialogQueryVariables
>;

const ADD_REFERRER_CODE_MUTATION = gql`
  mutation AddReferrerCodeMutation(
    $input: addReferrerCodeInput!
    $sport: Sport!
  ) {
    addReferrerCode(input: $input) {
      currentUser {
        slug
        referrerCodeDeadline
        ...RefereeExclusiveReward_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${RefereeExclusiveReward.fragments.currentUser}
` as TypedDocumentNode<
  AddReferrerCodeMutation,
  AddReferrerCodeMutationVariables
>;

const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: var(--double-unit);
  text-align: center;
  min-height: min(60vh, 460px);
  justify-content: flex-start;
`;

const Title = styled(Trapezoid)`
  --background: var(--c-set-surface-primary);
`;

const CodeInput = styled(TextInput)`
  height: 55px;
  margin-top: var(--double-unit);

  input {
    text-align: center;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onCodeAdded?: () => void;
};

export const SetInviteCodeDialog = ({ open, onClose, onCodeAdded }: Props) => {
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();
  const getCardSetConfig = useGetCardSetConfig();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rewardUser, setRewardUser] = useState<NonNullable<
    NonNullable<AddReferrerCodeMutation['addReferrerCode']>['currentUser']
  > | null>(null);

  const { data } = useQuery(SET_INVITE_CODE_DIALOG_QUERY, { skip: !open });
  const [addReferrerCode, { loading: submitting }] = useMutation(
    ADD_REFERRER_CODE_MUTATION,
    { showErrorsInForm: true }
  );

  const referrerCodeDeadline = data?.currentUser?.referrerCodeDeadline;

  const close = () => {
    setCode('');
    setError(null);
    onClose();
  };

  const onSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed || submitting) return;
    setError(null);

    const { data: mutationData, errors } = await addReferrerCode({
      variables: { input: { referrerCode: trimmed }, sport },
    });
    if (errors.length > 0) {
      setError(errors.map(e => e.message).join(', '));
      return;
    }

    onCodeAdded?.();
    const updatedUser = mutationData?.addReferrerCode?.currentUser;
    if (
      updatedUser?.inviteTaskAsReferee?.aasmState ===
      ManagerProgressionTaskState.COMPLETED
    ) {
      setRewardUser(updatedUser);
      return;
    }
    close();
  };

  if (rewardUser) {
    return (
      <StepperDialog
        onClose={() => {
          setRewardUser(null);
          close();
        }}
        steps={[
          {
            bodyElement: ({ onNext }) => (
              <Fragment key="referee-exclusive-reward">
                <RefereeExclusiveReward
                  currentUser={rewardUser}
                  onClose={onNext}
                />
              </Fragment>
            ),
          },
        ]}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      style={getCardSetConfig().visual.referral?.enterCodeBackground}
      footer={
        <Button
          size="large"
          color="primary"
          fullWidth
          onClick={onSubmit}
          disabled={!code.trim() || submitting}
        >
          <FormattedMessage {...glossary.submit} />
        </Button>
      }
    >
      <Content>
        {!!referrerCodeDeadline && (
          <ReferrerCodeCountdownChip deadline={referrerCodeDeadline} />
        )}
        <Title>
          <BrandM color="var(--c-black)">
            <Horizontal gap={0.5} center>
              <FontAwesomeIcon icon={iconGift} />
              <FormattedMessage defaultMessage="Enter your invite code" />
            </Horizontal>
          </BrandM>
        </Title>
        <BodyL>
          <FormattedMessage defaultMessage="You can receive codes from friends or affiliates who play Sorare Set and unlock rewards from them!" />
        </BodyL>
        <CodeInput
          name="code"
          value={code}
          onChange={e => {
            setError(null);
            setCode(e.target.value.toUpperCase());
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onSubmit();
            }
          }}
          placeholder={formatMessage({
            defaultMessage: 'Enter your invite code',
          })}
        />
        {!!error && <LabelM color="var(--c-red-400)">{error}</LabelM>}
      </Content>
    </Dialog>
  );
};
